import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap'
import { toast } from 'react-toastify'

import { AppDispatch, RootState, store } from 'store'
import { getErrorMessage, isValidOTP } from 'utils/helpers'
import { fetchContactsThunk } from 'slices/creators/thunk'
import { updateContactMobile } from 'services/api/creators'
import { generateContactMobileOTP } from 'services/api/auth'
import { resetCreatorsState } from 'slices/creators/reducer'
import { toggleUpdateCreatorProfileModal } from 'slices/ui/reducer'
import './AddContactMobileModal.scss'

const MOBILE_COUNTRY_CODE = '+91'
const INDIAN_MOBILE_REGEX = /^[6-9]\d{9}$/
const OTP_LENGTH = 6

const formatMobileDigits = (value: string) => {
    const digits = value.replace(/\D/g, '')
    const withoutCountryCode = digits.startsWith('91') ? digits.slice(2) : digits
    return withoutCountryCode.replace(/^0+/, '').slice(0, 10)
}

const getMobileValidationError = (mobile: string): string | undefined => {
    const trimmed = mobile.trim()
    if (!trimmed) return 'Mobile number is required.'
    if (!INDIAN_MOBILE_REGEX.test(trimmed)) {
        return 'Please enter a valid 10-digit mobile number.'
    }
    return undefined
}

type Step = 'mobile' | 'otp'

const AddContactMobileModal: React.FC<{
    isOpen: boolean
    onClose: () => void
    socialAccountId?: string
    creatorName?: string
}> = ({ isOpen, onClose, socialAccountId, creatorName }) => {
    const config = useSelector((state:RootState) => state.auth.selectedHub?.configurations)
    const mobileVerificationRequired = config?.['hub_creator_mobile_verification'] === 'y';

    const [processing, setProcessing] = useState(false)
    const [step, setStep] = useState<Step>('mobile')
    const [mobile, setMobile] = useState('')
    const [mobileError, setMobileError] = useState<string | undefined>()
    const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''))
    const [resendIn, setResendIn] = useState(0)
    const [resending, setResending] = useState(false)

    const dispatch = useDispatch<AppDispatch>()
    const selectedHubId = useSelector((state: RootState) => state.auth.selectedHubId)

    const resetForm = () => {
        setStep('mobile')
        setMobile('')
        setMobileError(undefined)
        setOtp(Array(OTP_LENGTH).fill(''))
        setResendIn(0)
        setResending(false)
    }

    useEffect(() => {
        if (isOpen) {
            resetForm()
        }
    }, [isOpen])

    useEffect(() => {
        let interval: ReturnType<typeof setInterval> | null = null
        if (resendIn > 0) {
            interval = setInterval(() => {
                setResendIn((prev) => prev - 1)
            }, 1000)
        }
        return () => {
            if (interval) clearInterval(interval)
        }
    }, [resendIn])

    const sendOTP = async () => {
        const error = getMobileValidationError(mobile)
        if (error) {
            setMobileError(error)
            return false
        }
        try {
            setProcessing(true)
            const success = await generateContactMobileOTP(mobile.trim())
            if (success) {
                setStep('otp')
                setResendIn(30)
                toast.success('OTP sent successfully.')
                return true
            }
            toast.error('Failed to send OTP. Please try again.')
            return false
        } catch (error: unknown) {
            console.error(error)
            toast.error(getErrorMessage(error) || 'Failed to send OTP. Please try again.')
            return false
        } finally {
            setProcessing(false)
        }
    }

    const handleResendOTP = async () => {
        if (resendIn || resending) return
        try {
            setResending(true)
            const success = await generateContactMobileOTP(mobile.trim())
            if (success) {
                setResendIn(30)
                toast.success('OTP has been resent!')
            }
        } catch (error: unknown) {
            console.error(error)
            toast.error(getErrorMessage(error) || 'Failed to resend OTP.')
        } finally {
            setResending(false)
        }
    }

    const handleSaveMobile = async (otpValue?: string) => {
        if (!selectedHubId || !socialAccountId) return

        const validationError = getMobileValidationError(mobile)
        if (validationError) {
            setMobileError(validationError)
            return
        }

        try {
            setProcessing(true)
            const payload = { mobile: mobile.trim(), ...(otpValue ? { otp: otpValue } : {}) }
            const response = await updateContactMobile(selectedHubId, socialAccountId, payload)
            if (response.influencer && response.influencer?.profile_completion !== 100) {
                dispatch(toggleUpdateCreatorProfileModal({ influencerId: response.influencer.uuid, creatorName }))
            }
            toast.success('Mobile number added successfully.')
            dispatch(resetCreatorsState())
            dispatch(fetchContactsThunk({ force: true, filters: store.getState().creators.filters }))
            onClose()
        } catch (error: unknown) {
            console.error(error)
            toast.error(getErrorMessage(error) || 'Failed to verify OTP. Please try again.')
        } finally {
            setProcessing(false)
        }
    }

    const handleVerifyAndSave = async (otpValue: string) => {
        await handleSaveMobile(otpValue)
    }

    const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const value = e.target.value.replace(/\D/g, '').slice(-1)
        const newOtp = [...otp]
        newOtp[index] = value
        setOtp(newOtp)

        if (value && index < OTP_LENGTH - 1) {
            const nextInput = document.getElementById(`contact-mobile-otp-${index + 1}`) as HTMLInputElement
            nextInput?.focus()
        }

        if (isValidOTP(newOtp)) {
            handleVerifyAndSave(newOtp.join(''))
        }
    }

    const handleOtpBackspace = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const previousInput = document.getElementById(`contact-mobile-otp-${index - 1}`) as HTMLInputElement
            previousInput?.focus()
        }
    }

    const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const pastedData = e.clipboardData.getData('Text').trim()
        if (pastedData.length === OTP_LENGTH && /^[0-9]+$/.test(pastedData)) {
            const newOtp = pastedData.split('')
            setOtp(newOtp)
            const lastInput = document.getElementById(`contact-mobile-otp-${OTP_LENGTH - 1}`) as HTMLInputElement
            lastInput?.focus()
            if (isValidOTP(newOtp)) {
                handleVerifyAndSave(newOtp.join(''))
            }
        }
        e.preventDefault()
    }

    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            centered
            className="add-contact-mobile-modal"
        >
            <ModalHeader toggle={onClose}>
                Add Mobile
                {creatorName && (
                    <p className="modal-subtitle text-dark fw-medium mb-0 mt-1">{creatorName}</p>
                )}
            </ModalHeader>
            <ModalBody>
                {step === 'mobile' ? (
                    <>
                        <p className="modal-subtitle">
                            {mobileVerificationRequired
                                ? "Enter the creator's mobile number to receive an OTP for verification."
                                : "Enter the creator's mobile number."}
                        </p>
                        <div className="add-contact-mobile-form">
                            <div className="mb-0">
                                <label htmlFor="contact-mobile" className="form-label">
                                    Mobile Number <span className="text-danger">*</span>
                                </label>
                                <div className="input-group mobile-input-group">
                                    <span
                                        className="input-group-text mobile-input-group__prefix"
                                        aria-hidden
                                    >
                                        {MOBILE_COUNTRY_CODE}
                                    </span>
                                    <input
                                        id="contact-mobile"
                                        value={mobile}
                                        onBlur={() => setMobileError(getMobileValidationError(mobile))}
                                        onChange={(e) => {
                                            setMobile(formatMobileDigits(e.target.value))
                                            setMobileError(undefined)
                                        }}
                                        type="tel"
                                        className="form-control"
                                        name="mobile"
                                        placeholder="9876543210"
                                        autoComplete="tel-national"
                                        inputMode="numeric"
                                        maxLength={10}
                                        aria-describedby={mobileError ? 'contact-mobile-error' : undefined}
                                    />
                                </div>
                                {mobileError && (
                                    <div id="contact-mobile-error" className="text-danger field-error">
                                        {mobileError}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="modal-subtitle">
                            Enter the 6-digit OTP sent to{' '}
                            <span className="fw-semibold">
                                {MOBILE_COUNTRY_CODE} {mobile}
                                <button
                                    type="button"
                                    className="unset-all text-primary ms-1"
                                    onClick={() => {
                                        setStep('mobile')
                                        setOtp(Array(OTP_LENGTH).fill(''))
                                    }}
                                    aria-label="Change mobile number"
                                >
                                    <i className="bx bx-edit fs-14" />
                                </button>
                            </span>
                        </p>
                        <Row className="g-2 justify-content-center add-contact-mobile-otp-row">
                            {otp.map((digit, index) => (
                                <Col key={index} xs="2">
                                    <input
                                        className="form-control text-center add-contact-mobile-otp-input"
                                        autoComplete="off"
                                        id={`contact-mobile-otp-${index}`}
                                        type="tel"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleOtpChange(e, index)}
                                        onKeyDown={(e) => handleOtpBackspace(e, index)}
                                        onPaste={index === 0 ? handleOtpPaste : undefined}
                                    />
                                </Col>
                            ))}
                        </Row>
                        <p className="text-center text-muted fs-13 mb-0 mt-3">
                            Didn&apos;t receive a code?{' '}
                            <button
                                type="button"
                                className={`unset-all ${resendIn || resending ? 'text-secondary' : 'text-primary'}`}
                                disabled={!!resendIn || resending}
                                onClick={handleResendOTP}
                            >
                                {resendIn ? `Resend in ${resendIn}s` : resending ? 'Sending...' : 'Resend OTP'}
                            </button>
                        </p>
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <button type="button" className="theme-btn btn-primary-outlined" onClick={onClose}>
                    Cancel
                </button>
                {step === 'mobile' ? (
                    <button
                        type="button"
                        className="theme-btn btn-primary"
                        onClick={mobileVerificationRequired ? sendOTP : () => handleSaveMobile()}
                        disabled={processing || !mobile.trim()}
                    >
                        {processing
                            ? mobileVerificationRequired
                                ? 'Sending...'
                                : 'Saving...'
                            : mobileVerificationRequired
                              ? 'Send OTP'
                              : 'Save'}
                    </button>
                ) : (
                    <button
                        type="button"
                        className="theme-btn btn-primary"
                        onClick={() => {
                            if (isValidOTP(otp)) handleVerifyAndSave(otp.join(''))
                        }}
                        disabled={processing || !isValidOTP(otp)}
                    >
                        {processing ? 'Verifying...' : 'Verify & Save'}
                    </button>
                )}
            </ModalFooter>
        </Modal>
    )
}

export default AddContactMobileModal
